/**
 * Touch-scrollable container for panels
 */

import * as PIXI from 'pixi.js';

export class ScrollContainer extends PIXI.Container {
  private contentContainer: PIXI.Container;
  private maskGfx: PIXI.Graphics;
  private viewHeight: number;
  private contentHeight = 0;
  private scrollY = 0;
  private velocity = 0;
  private isDragging = false;
  private lastY = 0;
  private lastTime = 0;

  constructor(width: number, height: number) {
    super();
    this.viewHeight = height;

    this.maskGfx = new PIXI.Graphics();
    this.maskGfx.beginFill(0xFFFFFF);
    this.maskGfx.drawRect(0, 0, width, height);
    this.maskGfx.endFill();
    this.addChild(this.maskGfx);

    this.contentContainer = new PIXI.Container();
    this.contentContainer.mask = this.maskGfx;
    this.addChild(this.contentContainer);

    this.eventMode = 'static';
    this.on('pointerdown', this.onDown, this);
    this.on('pointermove', this.onMove, this);
    this.on('pointerup', this.onUp, this);
    this.on('pointerupoutside', this.onUp, this);
  }

  get content(): PIXI.Container { return this.contentContainer; }

  setContentHeight(h: number): void {
    this.contentHeight = h;
    this.clampScroll();
  }

  resize(w: number, h: number): void {
    this.viewHeight = h;
    this.maskGfx.clear();
    this.maskGfx.beginFill(0xFFFFFF);
    this.maskGfx.drawRect(0, 0, w, h);
    this.maskGfx.endFill();
    this.clampScroll();
  }

  resetScroll(): void {
    this.scrollY = 0;
    this.velocity = 0;
    this.contentContainer.y = 0;
  }

  update(_dt: number): void {
    if (this.isDragging) return;

    if (Math.abs(this.velocity) > 0.5) {
      this.scrollY += this.velocity;
      this.velocity *= 0.92;
      this.clampScroll();
      this.contentContainer.y = -this.scrollY;
    } else {
      this.velocity = 0;
    }
  }

  private onDown(e: PIXI.FederatedPointerEvent): void {
    this.isDragging = true;
    this.velocity = 0;
    this.lastY = e.global.y;
    this.lastTime = performance.now();
  }

  private onMove(e: PIXI.FederatedPointerEvent): void {
    if (!this.isDragging) return;
    const dy = e.global.y - this.lastY;
    const now = performance.now();
    const dt = now - this.lastTime;

    this.scrollY -= dy;
    this.clampScroll();
    this.contentContainer.y = -this.scrollY;

    if (dt > 0) this.velocity = -dy / (dt / 16);

    this.lastY = e.global.y;
    this.lastTime = now;
  }

  private onUp(): void {
    this.isDragging = false;
  }

  private clampScroll(): void {
    const maxScroll = Math.max(0, this.contentHeight - this.viewHeight);
    this.scrollY = Math.max(0, Math.min(this.scrollY, maxScroll));
  }
}
